/**
 * ReadyResultPanel — Ergebnis nach Bulk-Freigabe im „Bereit"-Tab (T8):
 * applied/skipped + Push-Fehler-Block mit „Erneut senden" (`ReadyPushErrorsPanel`,
 * T14/P2) + „Alles rückgängig" (`ReadyUndoAllBar`, T13/P2, E5-Follow-up).
 * `aria-live="polite"`, da das Panel ohne Nutzerinteraktion nach der Mutation
 * erscheint (Muster Accessibility-Regel „Dynamische Inhalte").
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EID } from '@/lib/element-ids.registry'
import type { ApplyResult, PushError } from '@/lib/api/channel-content-bulk'

import { useUndoRetryMutations } from '../hooks/useUndoRetryMutations'
import { ReadyUndoAllBar } from './ReadyUndoAllBar'
import { ReadyPushErrorsPanel } from './ReadyPushErrorsPanel'

export interface ReadyResultPanelProps {
  result: ApplyResult
  appliedIds: string[]
}

export function ReadyResultPanel({ result, appliedIds }: ReadyResultPanelProps) {
  const { t } = useTranslation()
  const [pushErrors, setPushErrors] = useState<PushError[]>(result.push_errors)
  const [undoResult, setUndoResult] = useState<{ reverted: number; noPrevious: number } | null>(null)
  const { revert } = useUndoRetryMutations()

  const handleUndoAll = () => {
    revert.mutate(
      { suggestion_ids: appliedIds },
      { onSuccess: (r) => setUndoResult({ reverted: r.reverted, noPrevious: r.no_previous }) },
    )
  }

  return (
    <div
      id={EID['text-bulk-ready-result-region']}
      aria-live="polite"
      className="space-y-2 rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
    >
      <p className="text-sm font-medium text-green-700 dark:text-green-400">
        {t('channelContent.ready.partialResult', { applied: result.applied, skipped: result.skipped })}
      </p>
      {pushErrors.length > 0 && <ReadyPushErrorsPanel pushErrors={pushErrors} onResolved={setPushErrors} />}
      <ReadyUndoAllBar busy={revert.isPending} undoResult={undoResult} onUndoAll={handleUndoAll} />
    </div>
  )
}
